/* Copy da área Gestão de Staff (components/staff/): tabela de operadores + diálogos de troca de
   papel/reset de senha/criação + rótulos de papel/status. Nome do módulo evita colisão com o
   `staff.ts` da página `/staff` (routes/) — áreas distintas, escopos distintos (ADR-0811).

   `roleLabels`/`roleDescriptions`/`errors` usam campos em camelCase (`billingOps`, `emailTaken`)
   em vez de `Record<StaffRole, string>`/`Record<StaffErrorKey, string>`: as chaves reais do
   backend incluem snake_case (`billing_ops`, `email_taken`) e `useNamingConvention` rejeita
   propriedade de objeto snake_case. `staff-labels.ts`/`staff-error.ts` fazem o mapeamento via
   `switch` (mesmo idioma de `pack-submission-status.ts`). */
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

export type StaffErrorKey =
  | 'emailTaken'
  | 'lastSuperAdmin'
  | 'cannotSuspendSelf'
  | 'unauthorized'
  | 'notFound'
  | 'network'
  | 'default'
  | 'unexpected';

export interface StaffRoleLabels {
  readonly superAdmin: string;
  readonly support: string;
  readonly billingOps: string;
  readonly readonly: string;
}

export interface StaffStatusLabels {
  readonly active: string;
  readonly suspended: string;
}

interface StaffManagementContent {
  readonly roleLabels: StaffRoleLabels;
  readonly roleDescriptions: StaffRoleLabels;
  readonly statusLabels: StaffStatusLabels;
  readonly table: {
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly createButton: string;
    readonly columnEmail: string;
    readonly columnRole: string;
    readonly columnStatus: string;
    readonly columnCreatedAt: string;
    readonly actionsAriaLabel: (email: string) => string;
    readonly changeRoleAction: string;
    readonly resetPasswordAction: string;
    readonly suspendAction: string;
    readonly reactivateAction: string;
    readonly selectPageAriaLabel: string;
    readonly selectRowAriaLabel: (email: string) => string;
  };
  readonly createDialog: {
    readonly title: string;
    readonly description: string;
    readonly emailLabel: string;
    readonly emailPlaceholder: string;
    readonly roleLabel: string;
    readonly passwordLabel: string;
    readonly passwordPlaceholder: string;
    readonly validationError: string;
    readonly cancel: string;
    readonly submit: string;
    readonly success: (email: string) => string;
  };
  readonly changeRoleDialog: {
    readonly title: string;
    readonly description: (email: string) => string;
    readonly roleLabel: string;
    readonly cancel: string;
    readonly submit: string;
    readonly success: string;
  };
  readonly resetPasswordDialog: {
    readonly title: string;
    readonly description: (email: string) => string;
    readonly passwordLabel: string;
    readonly passwordPlaceholder: string;
    readonly cancel: string;
    readonly submit: string;
    readonly success: string;
  };
  readonly errors: Readonly<Record<StaffErrorKey, string>>;
}

const STAFF_MANAGEMENT_CONTENT: Bilingual<StaffManagementContent> = {
  'pt-BR': {
    roleLabels: {
      superAdmin: 'Super admin',
      support: 'Suporte',
      billingOps: 'Financeiro',
      readonly: 'Somente leitura',
    },
    roleDescriptions: {
      superAdmin: 'Acesso total, incluindo gestão de equipe.',
      support: 'Atendimento, moderação e suporte.',
      billingOps: 'Planos, faturamento e licenças.',
      readonly: 'Leitura de painéis e trilhas, sem ações.',
    },
    statusLabels: { active: 'Ativo', suspended: 'Suspenso' },
    table: {
      emptyTitle: 'Nenhum operador ainda',
      emptyDescription: 'Cadastre o primeiro membro da equipe do backoffice.',
      createButton: 'Novo membro',
      columnEmail: 'E-mail',
      columnRole: 'Papel',
      columnStatus: 'Status',
      columnCreatedAt: 'Criado em',
      actionsAriaLabel: (email) => `Ações de ${email}`,
      changeRoleAction: 'Trocar papel',
      resetPasswordAction: 'Resetar senha',
      suspendAction: 'Suspender',
      reactivateAction: 'Reativar',
      selectPageAriaLabel: 'Selecionar operadores visíveis',
      selectRowAriaLabel: (email) => `Selecionar ${email}`,
    },
    createDialog: {
      title: 'Novo membro da equipe',
      description:
        'Cadastre um operador do backoffice com uma senha inicial. Compartilhe a senha por um ' +
        'canal seguro; o operador pode ter a senha redefinida a qualquer momento.',
      emailLabel: 'E-mail',
      emailPlaceholder: 'operador@planuze.dev',
      roleLabel: 'Papel',
      passwordLabel: 'Senha inicial',
      passwordPlaceholder: 'Mínimo de 12 caracteres',
      validationError: 'Informe um e-mail válido e uma senha com pelo menos 12 caracteres.',
      cancel: 'Cancelar',
      submit: 'Criar',
      success: (email) => `Operador "${email}" criado.`,
    },
    changeRoleDialog: {
      title: 'Trocar papel',
      description: (email) => `Ajuste o nível de acesso de ${email}.`,
      roleLabel: 'Papel',
      cancel: 'Cancelar',
      submit: 'Salvar',
      success: 'Papel atualizado.',
    },
    resetPasswordDialog: {
      title: 'Resetar senha',
      description: (email) => `Defina uma nova senha para ${email}.`,
      passwordLabel: 'Nova senha',
      passwordPlaceholder: 'Mínimo de 12 caracteres',
      cancel: 'Cancelar',
      submit: 'Redefinir',
      success: 'Senha redefinida. Compartilhe-a por um canal seguro.',
    },
    errors: {
      emailTaken: 'Já existe um operador com esse e-mail.',
      lastSuperAdmin: 'Não é possível: isto deixaria a plataforma sem nenhum super admin ativo.',
      cannotSuspendSelf: 'Você não pode suspender a sua própria conta.',
      unauthorized: 'Você não tem permissão para gerenciar a equipe (ou sua sessão expirou).',
      notFound: 'Operador não encontrado. Atualize a lista e tente novamente.',
      network: 'Falha de conexão. Tente novamente.',
      default: 'Não foi possível concluir a ação. Tente novamente.',
      unexpected: 'Erro inesperado.',
    },
  },
  'en-US': {
    roleLabels: {
      superAdmin: 'Super admin',
      support: 'Support',
      billingOps: 'Billing',
      readonly: 'Read-only',
    },
    roleDescriptions: {
      superAdmin: 'Full access, including team management.',
      support: 'Support, moderation and customer care.',
      billingOps: 'Plans, billing and licenses.',
      readonly: 'Read access to dashboards and trails, no actions.',
    },
    statusLabels: { active: 'Active', suspended: 'Suspended' },
    table: {
      emptyTitle: 'No operators yet',
      emptyDescription: 'Add the first backoffice team member.',
      createButton: 'New member',
      columnEmail: 'Email',
      columnRole: 'Role',
      columnStatus: 'Status',
      columnCreatedAt: 'Created at',
      actionsAriaLabel: (email) => `Actions for ${email}`,
      changeRoleAction: 'Change role',
      resetPasswordAction: 'Reset password',
      suspendAction: 'Suspend',
      reactivateAction: 'Reactivate',
      selectPageAriaLabel: 'Select visible operators',
      selectRowAriaLabel: (email) => `Select ${email}`,
    },
    createDialog: {
      title: 'New team member',
      description:
        'Registers a backoffice operator with an initial password. Share the password over a ' +
        'secure channel; the password can be reset for the operator at any time.',
      emailLabel: 'Email',
      emailPlaceholder: 'operator@planuze.dev',
      roleLabel: 'Role',
      passwordLabel: 'Initial password',
      passwordPlaceholder: 'At least 12 characters',
      validationError: 'Enter a valid email and a password with at least 12 characters.',
      cancel: 'Cancel',
      submit: 'Create',
      success: (email) => `Operator "${email}" created.`,
    },
    changeRoleDialog: {
      title: 'Change role',
      description: (email) => `Adjust the access level of ${email}.`,
      roleLabel: 'Role',
      cancel: 'Cancel',
      submit: 'Save',
      success: 'Role updated.',
    },
    resetPasswordDialog: {
      title: 'Reset password',
      description: (email) => `Set a new password for ${email}.`,
      passwordLabel: 'New password',
      passwordPlaceholder: 'At least 12 characters',
      cancel: 'Cancel',
      submit: 'Reset',
      success: 'Password reset. Share it over a secure channel.',
    },
    errors: {
      emailTaken: 'An operator with this email already exists.',
      lastSuperAdmin: 'Not possible: this would leave the platform with no active super admin.',
      cannotSuspendSelf: 'You cannot suspend your own account.',
      unauthorized: 'You do not have permission to manage the team (or your session expired).',
      notFound: 'Operator not found. Refresh the list and try again.',
      network: 'Connection failed. Try again.',
      default: 'Could not complete the action. Try again.',
      unexpected: 'Unexpected error.',
    },
  },
};

export const useStaffManagementContent = (): StaffManagementContent =>
  useBilingual(STAFF_MANAGEMENT_CONTENT);

/** Exportado para os defaults pt-BR de `staff-error.ts`/`staff-labels.ts` (chamadores fora de
    componente não têm acesso ao hook). */
export { STAFF_MANAGEMENT_CONTENT };
